import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import Wishlist from './Wishlist';
import JournalEntryPanel from './JournalEntryPanel';
import { IconHeart, IconPlus, IconSettings, IconX, IconThumbUp, IconThumbDown } from './Icons';
import { Fragrance, JournalEntry, Ingredient } from '../App';
import { categoryToAxis, ARCHETYPES, ARCHETYPE_CENTERS } from '../archetypes';
import { buildLikedNotes, computeCompatScore, computeSignalModifier } from '../utils/scoring';

const SIZE = 180;
const C = SIZE / 2;
const SCALE = 68;
const toSvg = (v: number, axis: 'x' | 'y') => axis === 'x' ? C + v * SCALE : C - v * SCALE;

const JointCloud: React.FC<{
  myColor: string;
  myCx: number;
  myCy: number;
  mySpread: number;
  theirColor: string;
  theirArchetype: string;
}> = ({ myColor, myCx, myCy, mySpread, theirColor, theirArchetype }) => {
  const theirCenter = ARCHETYPE_CENTERS[theirArchetype] || { cx: 0, cy: 0 };
  const myR = Math.max(38, mySpread * SCALE * 3.2);
  const theirR = 42;
  const mySvgX = toSvg(myCx, 'x');
  const mySvgY = toSvg(myCy, 'y');
  const theirSvgX = toSvg(theirCenter.cx, 'x');
  const theirSvgY = toSvg(theirCenter.cy, 'y');

  return (
    <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} className="w-full">
      <defs>
        <radialGradient id="jc-my" cx={mySvgX} cy={mySvgY} r={myR} gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor={myColor} stopOpacity="0.85" />
          <stop offset="40%"  stopColor={myColor} stopOpacity="0.55" />
          <stop offset="75%"  stopColor={myColor} stopOpacity="0.18" />
          <stop offset="100%" stopColor={myColor} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="jc-their" cx={theirSvgX} cy={theirSvgY} r={theirR} gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor={theirColor} stopOpacity="0.85" />
          <stop offset="40%"  stopColor={theirColor} stopOpacity="0.55" />
          <stop offset="75%"  stopColor={theirColor} stopOpacity="0.18" />
          <stop offset="100%" stopColor={theirColor} stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Axis cross — very subtle */}
      <line x1={C} y1={6} x2={C} y2={SIZE-6} stroke="#D4CEBF" strokeWidth="0.5" />
      <line x1={6} y1={C} x2={SIZE-6} y2={C} stroke="#D4CEBF" strokeWidth="0.5" />

      {/* Blobs with multiply blend so overlap mixes the actual colors */}
      <rect x={0} y={0} width={SIZE} height={SIZE} fill="url(#jc-my)" style={{ mixBlendMode: 'multiply' }} />
      <rect x={0} y={0} width={SIZE} height={SIZE} fill="url(#jc-their)" style={{ mixBlendMode: 'multiply' }} />

      {/* Center pins */}
      <circle cx={mySvgX} cy={mySvgY} r={4} fill="none" stroke={myColor} strokeWidth="1.5" opacity="0.95" />
      <circle cx={mySvgX} cy={mySvgY} r={2} fill={myColor} opacity="0.95" />
      <circle cx={theirSvgX} cy={theirSvgY} r={4} fill="none" stroke={theirColor} strokeWidth="1.5" opacity="0.95" />
      <circle cx={theirSvgX} cy={theirSvgY} r={2} fill={theirColor} opacity="0.95" />
    </svg>
  );
};

const API_URL = 'https://7b3t8n3es9.execute-api.us-east-1.amazonaws.com/prod';

interface Connection {
  userId: string;
  memberNumber: number;
  displayName: string;
  status: 'accepted' | 'pending_in' | 'pending_out';
  since: string;
}

interface PublicProfile {
  memberNumber: number;
  userId: string;
  firstName: string;
  lastName?: string;
  archetypeName?: string;
  archetypeColor?: string;
  topFamilies: { category: string; pct: number }[];
  unseededFragsAdded: number;
  seededContributions: number;
  contributions: number;
  journalCount: number;
  createdAt?: string;
  firstCircle?: boolean;
  shareJournal?: boolean;
}

interface PublicJournalEntry {
  fragranceId: string;
  fragranceName: string;
  fragranceHouse?: string;
  rating: 'like' | 'love';
}

interface ScentSignals {
  preferredDescriptors?: string[];
  avoidedDescriptors?: string[];
  avoidedFamilies?: string[];
  preferredSeasons?: string[];
}

interface ScentProfileProps {
  userId: string;
  authToken: string;
  catalog: Fragrance[];
  journalEntries: JournalEntry[];
  ingredients: Ingredient[];
  onEditProfile: () => void;
  wishlistIds: Set<string>;
  onWishlistToggle: (fragranceId: string, fragranceName: string, fragranceHouse: string) => void;
  onAddToJournal: (fragrance: any) => void;
  onCatalogUpdated: () => void;
  onArchetypeDetected?: (name: string, color: string) => void;
  connections?: Connection[];
  onConnectionsChanged?: () => void;
  archetypeCounts?: Record<string, number>;
  activeMainTab?: 'myScent' | 'follow';
  topFamilies?: Array<{ category: string; pct: number }>;
  scentSignals?: ScentSignals | null;
  noteSentimentMap?: Record<string, number> | null;
}

interface Recommendation {
  name: string;
  house: string;
  reason: string;
}

interface Profile {
  profile: string;
  recommendations: Recommendation[];
  challenge: Recommendation;
  zodiac?: string;
  noteToSelf?: string[];
  generatedAt?: string;
  fragranceCount?: number;
}

const ScentProfile: React.FC<ScentProfileProps> = ({
  userId,
  authToken,
  catalog,
  journalEntries,
  ingredients,
  onEditProfile,
  wishlistIds,
  onWishlistToggle,
  onAddToJournal,
  onCatalogUpdated,
  onArchetypeDetected,
  connections = [],
  onConnectionsChanged,
  archetypeCounts = {},
  activeMainTab = 'myScent',
  topFamilies = [],
  scentSignals = null,
  noteSentimentMap = null,
}) => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [refreshesRemaining, setRefreshesRemaining] = useState<number>(0);
  const [wearLogsAtLastRefresh, setWearLogsAtLastRefresh] = useState<number>(0);
  const [activeSection, setActiveSection] = useState<'scent' | 'wishlist' | 'tray'>('scent');
  const [traySelected, setTraySelected] = useState<string | null>(null);
  const [trayPartnerSelected, setTrayPartnerSelected] = useState<string | null>(null);
  const [recFeedback, setRecFeedback] = useState<Record<string, 'up' | 'down'>>({});
  const [viewingMember, setViewingMember] = useState<PublicProfile | null>(null);
  const [friendJournal, setFriendJournal] = useState<PublicJournalEntry[]>([]);
  const [loadingMember, setLoadingMember] = useState(false);
  const [respondingTo, setRespondingTo] = useState<string | null>(null);
  const [addMemberInput, setAddMemberInput] = useState('');
  const [addMemberLoading, setAddMemberLoading] = useState(false);
  const [addMemberMessage, setAddMemberMessage] = useState('');
  const [hoveredDot, setHoveredDot] = useState<string | null>(null);
  const [selectedArchetypeIdx, setSelectedArchetypeIdx] = useState<number | null>(null);
  const [activeScrollSection, setActiveScrollSection] = useState('identity');
  const [resolvingJournal, setResolvingJournal] = useState<string | null>(null);
  const [trayJournalEntry, setTrayJournalEntry] = useState<JournalEntry | null>(null);
  const [localArchetypeCounts, setLocalArchetypeCounts] = useState<Record<string, number>>(archetypeCounts);
  const [openLayerPair, setOpenLayerPair] = useState<number | null>(null);

  type SectionKey = 'identity' | 'families' | 'map' | 'foryou' | 'challenge';
  const autoRefreshFired = useRef(false);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const scrollSectionRefs = useRef<Record<SectionKey, HTMLElement | null>>({
    identity: null, families: null, map: null, foryou: null, challenge: null,
  });
  const scrollNavRef = useRef<HTMLDivElement>(null);

  const scrollToSection = (key: SectionKey) => {
    const el = scrollSectionRefs.current[key];
    if (!el) return;
    const navBottom = scrollNavRef.current?.getBoundingClientRect().bottom ?? 110;
    const top = el.getBoundingClientRect().top + window.scrollY - navBottom - 8;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const scrollSections: Array<{ key: SectionKey; label: string }> = [
    { key: 'identity', label: 'Identity' },
    { key: 'families', label: 'Families' },
    { key: 'map', label: 'Your map' },
    { key: 'foryou', label: 'For you' },
    { key: 'challenge', label: 'Challenge' },
  ];

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    scrollSections.forEach(({ key }) => {
      const el = scrollSectionRefs.current[key];
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveScrollSection(key); },
        { threshold: 0.4 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach(o => o.disconnect());
  }, [profile]);

  useEffect(() => {
    fetchProfile();
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (!loading && !autoRefreshFired.current) {
      const totalSessions = journalEntries.reduce((sum, e) => sum + ((e.wearLogs?.length || 0) || 1), 0);
      // Auto-generate first profile when user has at least 1 journal entry (free — Lambda handles no decrement)
      if (!profile && totalSessions >= 1) {
        autoRefreshFired.current = true;
        generateProfile();
        return;
      }
      // Auto-refresh after every 10 new wear sessions
      if (profile) {
        const entriesSince = totalSessions - wearLogsAtLastRefresh;
        if (entriesSince >= 10) {
          autoRefreshFired.current = true;
          generateProfile();
        }
      }
    }
  }, [loading]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/users?userId=${userId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.data.scentProfile) setProfile(res.data.scentProfile);
      setRefreshesRemaining(res.data.refreshesRemaining ?? 0);
      setWearLogsAtLastRefresh(res.data.wearLogsAtLastRefresh ?? res.data.journalEntriesAtLastRefresh ?? 0);
      if (res.data.recommendationFeedback) setRecFeedback(res.data.recommendationFeedback);
      if (res.data.archetypeCounts && Object.keys(res.data.archetypeCounts).length > 0) {
        setLocalArchetypeCounts(res.data.archetypeCounts);
      }
      // Resume polling if profile was generating when page was loaded/refreshed
      if (res.data.profileGenerating) {
        setGenerating(true);
        startPolling(res.data.scentProfile?.generatedAt);
      }
    } catch (e) {
      console.log('No profile yet');
    }
    setLoading(false);
  };

  const startPolling = (previousGeneratedAt: string | undefined) => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    let attempts = 0;
    pollIntervalRef.current = setInterval(async () => {
      attempts++;
      if (attempts > 60) { // 60 * 3s = 3 min max
        clearInterval(pollIntervalRef.current!);
        setError('Something went wrong. Please try again.');
        setGenerating(false);
        return;
      }
      try {
        const res = await axios.get(`${API_URL}/users?userId=${userId}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const userData = res.data;
        if (!userData.profileGenerating && userData.scentProfile?.generatedAt !== previousGeneratedAt) {
          clearInterval(pollIntervalRef.current!);
          const newProfile = userData.scentProfile;
          setProfile(newProfile);
          if (userData.refreshesRemaining !== undefined) setRefreshesRemaining(userData.refreshesRemaining);
          const totalSessions = journalEntries.reduce((sum: number, e: any) => sum + ((e.wearLogs?.length || 0) || 1), 0);
          setWearLogsAtLastRefresh(totalSessions);
          setGenerating(false);
          // Auto-add new recommendations to catalog
          const allRecs = [...(newProfile.recommendations || []), ...(newProfile.challenge ? [newProfile.challenge] : [])];
          let catalogUpdated = false;
          let autoAdded = 0;
          for (const rec of allRecs) {
            if (autoAdded >= 2) break;
            const found = catalog.find((f: any) => f.name.toLowerCase() === rec.name.toLowerCase());
            if (!found) {
              try {
                await axios.post(`${API_URL}/fragrances`, { name: `${rec.name} by ${rec.house}` }, {
                  headers: { Authorization: `Bearer ${authToken}` },
                });
                catalogUpdated = true;
                autoAdded++;
              } catch { /* non-blocking */ }
            }
          }
          if (catalogUpdated) onCatalogUpdated();
        }
      } catch { /* poll silently */ }
    }, 3000);
  };

  const generateProfile = async () => {
    setGenerating(true);
    setError('');
    const previousGeneratedAt = profile?.generatedAt;
    try {
      const res = await axios.post(`${API_URL}/profile`, { userId }, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (res.status === 202) {
        // Async — profile is generating in background, start polling
        if (res.data.refreshesRemaining !== undefined) setRefreshesRemaining(res.data.refreshesRemaining);
        startPolling(previousGeneratedAt);
        return; // stay in generating state, polling will clear it
      }

      // Legacy sync path (fallback)
      const newProfile = {
        ...res.data.profile,
        generatedAt: new Date().toISOString(),
        fragranceCount: journalEntries.length,
      };
      setProfile(newProfile);
      if (res.data.refreshesRemaining !== undefined) setRefreshesRemaining(res.data.refreshesRemaining);
      const totalSessions = journalEntries.reduce((sum, e) => sum + ((e.wearLogs?.length || 0) || 1), 0);
      setWearLogsAtLastRefresh(totalSessions);

      const allRecs = [
        ...(newProfile.recommendations || []),
        ...(newProfile.challenge ? [newProfile.challenge] : []),
      ];
      let catalogUpdated = false;
      let autoAdded = 0;
      for (const rec of allRecs) {
        if (autoAdded >= 2) break;
        const found = catalog.find(f =>
          f.name.toLowerCase() === rec.name.toLowerCase()
        );
        if (!found) {
          try {
            await axios.post(`${API_URL}/fragrances`, { name: `${rec.name} by ${rec.house}` }, {
              headers: { Authorization: `Bearer ${authToken}` },
            });
            catalogUpdated = true;
            autoAdded++;
          } catch { /* non-blocking */ }
        }
      }
      if (catalogUpdated) onCatalogUpdated();
    } catch (e: any) {
      if (e.response?.status === 429) {
        const data = e.response.data;
        setError(`No refreshes remaining. Add ${data.entriesUntilAuto} more fragrance${data.entriesUntilAuto === 1 ? '' : 's'} to unlock an auto-refresh.`);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setGenerating(false);
    }
  };

  const resolveFragranceId = (name: string, house: string): string => {
    const match = catalog.find(f =>
      f.name.toLowerCase() === name.toLowerCase() ||
      (f.name.toLowerCase().includes(name.toLowerCase()) && f.house.toLowerCase().includes(house.toLowerCase()))
    );
    if (match) return match.fragranceId;
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  };

  const resolveRealId = async (name: string, house: string): Promise<{ fragranceId: string; catalogUpdated: boolean }> => {
    let fragranceId = resolveFragranceId(name, house);
    const isInCatalog = catalog.some(f => f.fragranceId === fragranceId);
    if (!isInCatalog) {
      try {
        const res = await axios.post(`${API_URL}/fragrances`, { name: `${name} by ${house}` }, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (res.data.found && res.data.fragranceId) {
          return { fragranceId: res.data.fragranceId, catalogUpdated: true };
        }
      } catch { /* use fallback id */ }
    }
    return { fragranceId, catalogUpdated: false };
  };

  const handleAddRecToJournal = async (name: string, house: string) => {
    setResolvingJournal(`${name}-${house}`);
    const { fragranceId, catalogUpdated } = await resolveRealId(name, house);
    if (catalogUpdated) onCatalogUpdated();
    setResolvingJournal(null);
    onAddToJournal({ fragranceId, name, house });
  };

  const handleWishlistRec = async (name: string, house: string) => {
    const { fragranceId, catalogUpdated } = await resolveRealId(name, house);
    if (catalogUpdated) onCatalogUpdated();
    onWishlistToggle(fragranceId, name, house);
  };

  const handleRecFeedback = async (name: string, house: string, vote: 'up' | 'down') => {
    const fragKey = `${name}|${house}`;
    const current = recFeedback[fragKey];
    const newVote = current === vote ? undefined : vote; // toggle off if same vote
    const next = { ...recFeedback };
    if (newVote) next[fragKey] = newVote; else delete next[fragKey];
    setRecFeedback(next);
    try {
      if (newVote) {
        await axios.put(`${API_URL}/users`, { userId, action: 'recordRecommendationFeedback', fragKey, vote: newVote }, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
      }
    } catch (e) { console.error('Feedback error', e); }
  };

  const timeAgo = (isoString: string) => {
    const seconds = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const recommendedIds = useMemo(() => {
    if (!profile) return new Set<string>();
    return new Set(profile.recommendations.map(r => resolveFragranceId(r.name, r.house)));
  }, [profile, catalog]);

  // Compute match scores for recommendations + challenge
  const recMatchScores = useMemo(() => {
    if (!profile || topFamilies.length === 0) return new Map<string, number>();
    const likedNotes = buildLikedNotes(journalEntries, catalog, noteSentimentMap || undefined);
    const scores = new Map<string, number>();
    const allRecs = [...(profile.recommendations || []), ...(profile.challenge ? [profile.challenge] : [])];
    for (const rec of allRecs) {
      const frag = catalog.find(f =>
        f.name?.toLowerCase() === rec.name.toLowerCase() &&
        f.house?.toLowerCase() === rec.house.toLowerCase()
      );
      if (!frag) continue;
      const base = computeCompatScore(frag, topFamilies, likedNotes);
      if (base === null) continue;
      const modifier = scentSignals ? computeSignalModifier(frag, scentSignals) : 0;
      scores.set(`${rec.name}|${rec.house}`, Math.round(Math.max(0, Math.min(100, base + modifier))));
    }
    return scores;
  }, [profile, catalog, journalEntries, topFamilies, scentSignals]);

  const challengeId = useMemo(() => {
    if (!profile?.challenge) return null;
    return resolveFragranceId(profile.challenge.name, profile.challenge.house);
  }, [profile, catalog]);

  const scentFamilyData = useMemo(() => {
    const likedEntries = journalEntries.filter(e => e.rating === 'like' || e.rating === 'love');
    const categoryCounts: Record<string, number> = {};

    for (const entry of likedEntries) {
      const frag = catalog.find(f => f.fragranceId === entry.fragranceId);
      if (!frag) continue;
      const allNotes = [...(frag.topNotes || []), ...(frag.middleNotes || []), ...(frag.baseNotes || []), ...(frag.notes || [])];
      for (const note of allNotes) {
        const id = note.toLowerCase().replace(/\s+/g, '-');
        const ing = ingredients.find(i => i.ingredientId === id);
        if (ing?.category) {
          categoryCounts[ing.category] = (categoryCounts[ing.category] || 0) + 1;
        }
      }
    }

    const total = Object.values(categoryCounts).reduce((sum, n) => sum + n, 0);
    if (total === 0) return [];

    return Object.entries(categoryCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 6)
      .map(([category, count]) => ({
        category,
        count,
        pct: Math.round((count / total) * 100),
      }));
  }, [journalEntries, catalog, ingredients]);

  // Scent identity map data
  const scentMapData = useMemo(() => {
    const scoredEntries = journalEntries.filter(e => e.rating === 'like' || e.rating === 'neutral' || e.rating === 'love');
    if (scoredEntries.length === 0) return null;

    const dots: Array<{ id: string; name: string; x: number; y: number; rating: string }> = [];

    for (const entry of scoredEntries) {
      const frag = catalog.find(f => f.fragranceId === entry.fragranceId);
      if (!frag) continue;

      const allNotes = [...(frag.topNotes || []), ...(frag.middleNotes || []), ...(frag.baseNotes || []), ...(frag.notes || [])];
      const axisScores: Array<{ x: number; y: number }> = [];

      for (const note of allNotes) {
        const id = note.toLowerCase().replace(/\s+/g, '-');
        const ing = ingredients.find(i => i.ingredientId === id || i.name.toLowerCase() === note.toLowerCase());
        if (ing?.category) {
          axisScores.push(categoryToAxis(ing.category, ing.name));
        }
      }

      if (axisScores.length === 0) continue;

      const avgX = axisScores.reduce((s, a) => s + a.x, 0) / axisScores.length;
      const avgY = axisScores.reduce((s, a) => s + a.y, 0) / axisScores.length;

      dots.push({
        id: entry.fragranceId,
        name: frag.name,
        x: avgX,
        y: avgY,
        rating: entry.rating,
      });
    }

    if (dots.length === 0) return null;

    const likedDots = dots.filter(d => d.rating === 'like' || d.rating === 'love');
    const sourceDots = likedDots.length > 0 ? likedDots : dots;

    const cx = sourceDots.reduce((s, d) => s + d.x, 0) / sourceDots.length;
    const cy = sourceDots.reduce((s, d) => s + d.y, 0) / sourceDots.length;

    const spread = Math.sqrt(
      sourceDots.reduce((s, d) => s + Math.pow(d.x - cx, 2) + Math.pow(d.y - cy, 2), 0) / sourceDots.length
    );

    const archetype = ARCHETYPES.find(a => a.test(cx, cy, spread, sourceDots.length))!;

    return { dots, cx, cy, spread, archetype };
  }, [journalEntries, catalog, ingredients]);

  const myTopFamilies = useMemo(() => {
    const likedEntries = journalEntries.filter(e => e.rating === 'like' || e.rating === 'love');
    const counts: Record<string, number> = {};
    for (const entry of likedEntries) {
      const frag = catalog.find(f => f.fragranceId === entry.fragranceId);
      if (!frag) continue;
      const allNotes = [...(frag.topNotes || []), ...(frag.middleNotes || []), ...(frag.baseNotes || []), ...(frag.notes || [])];
      for (const note of allNotes) {
        const ing = ingredients.find(i => i.ingredientId === note.toLowerCase().replace(/\s+/g, '-') || i.name.toLowerCase() === note.toLowerCase());
        if (ing?.category) counts[ing.category] = (counts[ing.category] || 0) + 1;
      }
    }
    const total = Object.values(counts).reduce((s, n) => s + n, 0);
    if (total === 0) return [];
    return Object.entries(counts).sort(([, a], [, b]) => b - a).slice(0, 3)
      .map(([cat, count]) => ({ category: cat, pct: Math.round((count / total) * 100) }));
  }, [journalEntries, catalog, ingredients]);

  useEffect(() => {
    if (!scentMapData || !userId || !authToken) return;
    if (onArchetypeDetected) {
      onArchetypeDetected(scentMapData.archetype.name, scentMapData.archetype.color);
    }
    axios.put(`${API_URL}/users`, {
      userId,
      action: 'updateArchetype',
      archetypeName: scentMapData.archetype.name,
      archetypeColor: scentMapData.archetype.color,
      topFamilies: myTopFamilies,
      journalCount: journalEntries.length,
    }, { headers: { Authorization: `Bearer ${authToken}` } }).catch(() => {});
  }, [scentMapData, myTopFamilies]);

  const computeCompatibility = (
    myArchetype: string | undefined, myFamilies: { category: string; pct: number }[],
    theirArchetype: string | undefined, theirFamilies: { category: string; pct: number }[]
  ) => {
    let familyScore = 0;
    for (const mine of myFamilies) {
      const theirs = theirFamilies.find(f => f.category === mine.category);
      if (theirs) familyScore += Math.min(mine.pct, theirs.pct);
    }
    familyScore = Math.min(60, familyScore);
    const HARMONY: Record<string, Record<string, number>> = {
      'The Romantic': { 'The Maximalist': 35, 'The Alchemist': 38, 'The Romantic': 40 },
      'The Minimalist': { 'The Aura': 38, 'The Minimalist': 40, 'The Collector': 30 },
      'The Collector': { 'The Chameleon': 38, 'The Alchemist': 35, 'The Collector': 40 },
      'The Signature': { 'The Signature': 40, 'The Curator': 35, 'The Contrarian': 28 },
      'The Aura': { 'The Romantic': 38, 'The Aura': 40, 'The Minimalist': 36 },
      'The Maximalist': { 'The Contrarian': 35, 'The Maximalist': 40, 'The Romantic': 34 },
      'The Alchemist': { 'The Collector': 36, 'The Romantic': 38, 'The Alchemist': 40 },
      'The Chameleon': { 'The Collector': 38, 'The Chameleon': 40, 'The Alchemist': 32 },
      'The Contrarian': { 'The Maximalist': 35, 'The Contrarian': 40, 'The Signature': 28 },
      'The Curator': { 'The Signature': 36, 'The Curator': 40, 'The Aura': 34 },
    };
    const archetypeScore = myArchetype && theirArchetype ? (HARMONY[myArchetype]?.[theirArchetype] ?? 20) : 20;
    const total = Math.round(familyScore + archetypeScore);
    let title: string, description: string;
    if (total >= 80) { title = 'Kindred Noses'; description = 'Your taste profiles mirror each other with rare precision — you likely reach for the same bottles without knowing why.'; }
    else if (total >= 65) { title = 'Complementary Souls'; description = 'You share enough common ground to understand each other, with just enough difference to keep things interesting.'; }
    else if (total >= 50) { title = 'Curious Contrast'; description = "You come from different scent worlds, but that's exactly what makes your pairing intriguing — you'll discover new things through each other."; }
    else if (total >= 35) { title = 'Opposites Attract'; description = "Your tastes pull in different directions — but in perfumery, opposites often make the most memorable combinations."; }
    else { title = 'Uncharted Territory'; description = "You two are scent explorers of entirely different continents — a rare pairing that could lead to unexpected discoveries."; }
    return { score: total, title, description };
  };

  const handleSendConnectionRequest = async () => {
    const num = parseInt(addMemberInput.replace('#', '').trim());
    if (!num || isNaN(num)) { setAddMemberMessage('Enter a valid member number'); return; }
    setAddMemberLoading(true);
    setAddMemberMessage('');
    try {
      await axios.put(`${API_URL}/users`, {
        userId, action: 'sendConnectionRequest', targetMemberNumber: num,
      }, { headers: { Authorization: `Bearer ${authToken}` } });
      setAddMemberMessage('Request sent!');
      setAddMemberInput('');
      if (onConnectionsChanged) onConnectionsChanged();
    } catch (e: any) {
      setAddMemberMessage(e.response?.data?.message || 'Something went wrong');
    }
    setAddMemberLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <p className="text-stone">Loading...</p>
      </div>
    );
  }

  // SVG constants
  const SVG_SIZE = 260;
  const CENTER = SVG_SIZE / 2;
  const SCALE = 100;
  const toSvg = (v: number, axis: 'x' | 'y') =>
    axis === 'x' ? CENTER + v * SCALE : CENTER - v * SCALE;

  return (
    <div className="min-h-screen bg-cream pb-24 md:pb-10">
      <div className="max-w-2xl mx-auto px-6 py-10">

        {/* Header */}
        {activeMainTab === 'myScent' && (
          <div className="flex justify-between items-start mb-8">
            <h2 className="font-serif text-3xl text-charcoal">My Scent</h2>
            <button
              onClick={onEditProfile}
              className="text-stone-light hover:text-charcoal transition-colors"
              title="Settings"
            >
              <IconSettings size={18} />
            </button>
          </div>
        )}

        {/* Section toggle — only shown when on My Scent main tab */}
        {activeMainTab === 'myScent' && (
          <div className="flex gap-6 mb-8 border-b border-cream-dark overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
            {[
              { key: 'scent' as const, label: 'Scent Identity' },
              { key: 'wishlist' as const, label: `Wishlist${wishlistIds.size > 0 ? ` (${wishlistIds.size})` : ''}` },
              { key: 'tray' as const, label: 'Scent Tray' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setActiveSection(key)}
                className={`text-xs tracking-widest uppercase pb-3 flex-shrink-0 transition-colors ${
                  activeSection === key ? 'text-charcoal border-b-2 border-charcoal' : 'text-stone-light hover:text-stone'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {activeMainTab === 'follow' && (() => {
          const accepted = connections.filter(c => c.status === 'accepted');
          const pendingIn = connections.filter(c => c.status === 'pending_in');
          const pendingOut = connections.filter(c => c.status === 'pending_out');

          const handleRespond = async (fromUserId: string, accept: boolean) => {
            setRespondingTo(fromUserId);
            try {
              await axios.put(`${API_URL}/users`, {
                userId, action: 'respondConnectionRequest', fromUserId, accept,
              }, { headers: { Authorization: `Bearer ${authToken}` } });
              if (onConnectionsChanged) onConnectionsChanged();
            } catch (e) { console.error(e); }
            setRespondingTo(null);
          };

          const handleRemove = async (targetUserId: string) => {
            try {
              await axios.put(`${API_URL}/users`, {
                userId, action: 'removeConnection', targetUserId,
              }, { headers: { Authorization: `Bearer ${authToken}` } });
              if (onConnectionsChanged) onConnectionsChanged();
            } catch (e) { console.error(e); }
          };

          const handleViewMember = async (conn: Connection) => {
            setLoadingMember(true);
            setFriendJournal([]);
            try {
              const res = await axios.get(`${API_URL}/users?memberNumber=${conn.memberNumber}`, {
                headers: { Authorization: `Bearer ${authToken}` },
              });
              const profile: PublicProfile = {
                ...res.data,
                contributions: (res.data.unseededFragsAdded || 0) + (res.data.seededContributions || 0),
              };
              setViewingMember(profile);
              if (profile.shareJournal !== false && profile.userId) {
                try {
                  const jRes = await axios.get(`${API_URL}/journal?userId=${profile.userId}&public=true`, {
                    headers: { Authorization: `Bearer ${authToken}` },
                  });
                  setFriendJournal(jRes.data.items || []);
                } catch (e) { /* journal fetch failing shouldn't break the card */ }
              }
            } catch (e) { console.error(e); }
            setLoadingMember(false);
          };

          return (
            <div>
              <h1 className="font-serif text-3xl text-charcoal mb-6">Follow My Scent</h1>
              {/* Pending incoming */}
              {pendingIn.length > 0 && (
                <div className="mb-8">
                  <p className="text-xs tracking-widest uppercase text-stone-light mb-4">
                    New requests ({pendingIn.length})
                  </p>
                  <div className="space-y-3">
                    {pendingIn.map(c => (
                      <div key={c.userId} className="bg-white border border-cream-dark rounded-xl px-5 py-4 flex items-center justify-between">
                        <div>
                          <p className="font-serif text-lg text-charcoal">{c.displayName}</p>
                          <p className="text-xs text-stone-light">Member #{String(c.memberNumber).padStart(4, '0')}</p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleRespond(c.userId, true)}
                            disabled={respondingTo === c.userId}
                            className="text-xs tracking-widest uppercase px-4 py-2 bg-charcoal text-cream rounded-xl hover:bg-charcoal-light transition-colors disabled:opacity-50"
                          >
                            Accept
                          </button>
                          <button
                            onClick={() => handleRespond(c.userId, false)}
                            disabled={respondingTo === c.userId}
                            className="text-xs tracking-widest uppercase px-4 py-2 border border-cream-dark text-stone rounded-xl hover:border-stone transition-colors disabled:opacity-50"
                          >
                            Decline
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Connected members */}
              {accepted.length > 0 ? (
                <div className="mb-8">
                  <p className="text-xs tracking-widest uppercase text-stone-light mb-4">
                    Following ({accepted.length})
                  </p>
                  <div className="space-y-3">
                    {accepted.map(c => (
                      <div key={c.userId} className="bg-white border border-cream-dark rounded-xl px-5 py-4 flex items-center justify-between">
                        <button
                          className="text-left flex-1"
                          onClick={() => handleViewMember(c)}
                          disabled={loadingMember}
                        >
                          <p className="font-serif text-lg text-charcoal">{c.displayName}</p>
                          <p className="text-xs text-stone-light">Member #{String(c.memberNumber).padStart(4, '0')}</p>
                        </button>
                        <button
                          onClick={() => handleRemove(c.userId)}
                          className="text-stone-light hover:text-charcoal transition-colors ml-3"
                          title="Remove"
                        >
                          <IconX size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : pendingIn.length === 0 && (
                <div className="text-center py-12">
                  <p className="font-serif text-2xl text-charcoal mb-3">No connections yet</p>
                  <p className="text-stone text-sm">Add someone below by their member number.</p>
                </div>
              )}

              {/* Pending outgoing */}
              {pendingOut.length > 0 && (
                <div className="mb-8">
                  <p className="text-xs tracking-widest uppercase text-stone-light mb-3">Awaiting approval</p>
                  <div className="space-y-2">
                    {pendingOut.map(c => (
                      <div key={c.userId} className="flex items-center justify-between text-stone-light text-sm px-1">
                        <span>{c.displayName} · #{String(c.memberNumber).padStart(4, '0')}</span>
                        <button onClick={() => handleRemove(c.userId)} className="hover:text-charcoal transition-colors ml-3"><IconX size={14} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add a member */}
              <div className="border-t border-cream-dark pt-6">
                <p className="text-xs tracking-widest uppercase text-stone-light mb-3">Add a member</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={addMemberInput}
                    onChange={e => { setAddMemberInput((e.target as HTMLInputElement).value); setAddMemberMessage(''); }}
                    placeholder="Member # (e.g. 0002)"
                    className="flex-1 bg-white border border-cream-dark rounded-xl px-4 py-3 text-sm text-charcoal placeholder-stone-light focus:outline-none focus:border-stone transition-colors"
                  />
                  <button
                    onClick={handleSendConnectionRequest}
                    disabled={addMemberLoading || !addMemberInput.trim()}
                    className="px-5 py-3 bg-charcoal text-cream text-xs tracking-widest uppercase rounded-xl hover:bg-charcoal-light transition-colors disabled:opacity-50"
                  >
                    {addMemberLoading ? '...' : 'Add'}
                  </button>
                </div>
                {addMemberMessage && (
                  <p className={`text-xs mt-2 ${addMemberMessage === 'Request sent!' ? 'text-stone' : 'text-red-400'}`}>
                    {addMemberMessage}
                  </p>
                )}
                <p className="text-xs text-stone-light mt-2">They'll need to approve before you're connected.</p>
              </div>

              {/* View member overlay */}
              {viewingMember && (() => {
                const fColor = viewingMember.archetypeColor || '#F0C419';
                const fContribs = viewingMember.contributions || 0;
                const fJoinYear = viewingMember.createdAt ? new Date(viewingMember.createdAt).getFullYear() : 2026;
                const fYears = new Date().getFullYear() - fJoinYear + 1;
                const fBadgeGroups = [
                  {
                    section: 'Membership',
                    badges: [
                      viewingMember.memberNumber <= 2 && { accent: fColor, label: 'Founder' },
                      viewingMember.firstCircle && viewingMember.memberNumber > 2 && { accent: '#9B6FD6', label: 'First Circle' },
                    ].filter(Boolean) as { accent: string; label: string }[],
                  },
                  {
                    section: 'Contributions',
                    badges: [
                      fContribs >= 500 && { accent: '#2C2C2C', label: '500' },
                      fContribs >= 250 && fContribs < 500 && { accent: '#2C2C2C', label: '250' },
                      fContribs >= 100 && fContribs < 250 && { accent: '#2C2C2C', label: '100' },
                      fContribs >= 50 && fContribs < 100 && { accent: '#2C2C2C', label: '50' },
                      fContribs >= 10 && fContribs < 50 && { accent: '#2C2C2C', label: '10' },
                    ].filter(Boolean) as { accent: string; label: string }[],
                  },
                  {
                    section: 'Years',
                    badges: fJoinYear === 2026 ? [{ accent: '#B0A090', label: 'Year 1' }] : [],
                  },
                ].filter(g => g.badges.length > 0);
                const compat = computeCompatibility(scentMapData?.archetype.name, myTopFamilies, viewingMember.archetypeName, viewingMember.topFamilies);
                const myColor = scentMapData?.archetype.color || '#F0C419';
                return (
                  <div className="fixed inset-0 bg-charcoal bg-opacity-50 z-50 flex items-center justify-center px-4" onClick={() => setViewingMember(null)}>
                    <div className="relative w-full max-w-sm bg-cream rounded-2xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                      <div className="h-1.5 w-full" style={{ background: `linear-gradient(to right, ${fColor}88, ${fColor})` }} />
                      <button onClick={() => setViewingMember(null)} className="absolute top-4 right-4 text-stone-light hover:text-charcoal transition-colors z-10"><IconX size={18} /></button>
                      <div className="px-6 pt-6 pb-8">

                        {/* Identity */}
                        <div className="mb-5">
                          <p className="text-xs tracking-widest uppercase text-stone-light mb-1">Member #{String(viewingMember.memberNumber).padStart(4, '0')}</p>
                          <h2 className="font-serif text-3xl text-charcoal">{viewingMember.firstName}{viewingMember.lastName ? ` ${viewingMember.lastName}` : ''}</h2>
                          {viewingMember.archetypeName && (
                            <div className="flex items-center gap-2 mt-1">
                              <span className="inline-block w-2 h-2 rounded-full" style={{ background: fColor }} />
                              <p className="text-sm text-stone">{viewingMember.archetypeName}</p>
                            </div>
                          )}
                        </div>

                        {/* Top families */}
                        {viewingMember.topFamilies.length > 0 && (
                          <div className="mb-5">
                            <p className="text-xs tracking-widest uppercase text-stone-light mb-3">Top scent families</p>
                            <div className="space-y-2">
                              {viewingMember.topFamilies.map(f => (
                                <div key={f.category}>
                                  <div className="flex justify-between mb-1"><span className="text-xs text-charcoal">{f.category}</span><span className="text-xs text-stone-light">{f.pct}%</span></div>
                                  <div className="h-1 bg-cream-dark rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${f.pct}%`, background: fColor }} /></div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Contributions + Journal */}
                        {(fContribs > 0 || viewingMember.journalCount > 0) && (
                          <div className="mb-5 flex gap-3">
                            {fContribs > 0 && (
                              <div className="flex-1 bg-white border border-cream-dark rounded-xl px-4 py-3 text-center">
                                <p className="font-serif text-2xl text-charcoal">{fContribs}</p>
                                <p className="text-xs tracking-widest uppercase text-stone-light mt-0.5">Catalog Contributions</p>
                              </div>
                            )}
                            {viewingMember.journalCount > 0 && (
                              <div className="flex-1 bg-white border border-cream-dark rounded-xl px-4 py-3 text-center">
                                <p className="font-serif text-2xl text-charcoal">{viewingMember.journalCount}</p>
                                <p className="text-xs tracking-widest uppercase text-stone-light mt-0.5">Journal Entries</p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Badges */}
                        {fBadgeGroups.length > 0 && (
                          <div className="mb-6">
                            <p className="text-xs tracking-widest uppercase text-stone-light mb-3">Badges</p>
                            <div className="flex flex-wrap gap-2">
                              {fBadgeGroups.flatMap(g => g.badges).map((b, i) => (
                                <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border" style={{ borderColor: b.accent + '55', background: b.accent + '0D' }}>
                                  <svg width="10" height="10" viewBox="0 0 44 42" fill="none">
                                    <polygon points="22,2 27.5,16.5 43,16.5 30.5,25.5 35.5,40 22,31 8.5,40 13.5,25.5 1,16.5 16.5,16.5"
                                      fill={b.accent} stroke={b.accent} strokeWidth="2" strokeLinejoin="round" />
                                  </svg>
                                  <span className="text-xs tracking-wide" style={{ color: b.accent }}>{b.label}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Compatibility */}
                        <div className="bg-white border border-cream-dark rounded-xl overflow-hidden">
                          <p className="text-xs tracking-widest uppercase text-stone-light px-5 pt-5 mb-3">Scent Compatibility</p>

                          {/* Joint cloud map */}
                          {scentMapData && viewingMember.archetypeName && (
                            <div className="px-4 pb-2">
                              <JointCloud
                                myColor={myColor}
                                myCx={scentMapData.cx}
                                myCy={scentMapData.cy}
                                mySpread={scentMapData.spread}
                                theirColor={fColor}
                                theirArchetype={viewingMember.archetypeName}
                              />
                              <div className="flex justify-between items-center mt-1 px-1">
                                <span className="text-xs flex items-center gap-1.5" style={{ color: myColor }}>
                                  <span className="inline-block w-2 h-2 rounded-full" style={{ background: myColor }} />
                                  You
                                </span>
                                <span className="text-xs flex items-center gap-1.5" style={{ color: fColor }}>
                                  {viewingMember.firstName}
                                  <span className="inline-block w-2 h-2 rounded-full" style={{ background: fColor }} />
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Score + interpretation */}
                          <div className="flex items-center gap-4 px-5 py-4 border-t border-cream-dark mt-3">
                            <div className="relative w-14 h-14 flex-shrink-0">
                              <svg viewBox="0 0 64 64" className="w-full h-full -rotate-90">
                                <circle cx="32" cy="32" r="26" fill="none" stroke="#F0EDE8" strokeWidth="6" />
                                <circle cx="32" cy="32" r="26" fill="none" stroke={myColor} strokeWidth="6"
                                  strokeDasharray={`${(compat.score / 100) * 163} 163`} strokeLinecap="round" />
                              </svg>
                              <span className="absolute inset-0 flex items-center justify-center font-serif text-base text-charcoal">{compat.score}</span>
                            </div>
                            <div>
                              <p className="font-serif text-lg text-charcoal">{compat.title}</p>
                              <p className="text-stone text-xs leading-relaxed mt-1">{compat.description}</p>
                            </div>
                          </div>
                        </div>

                        {/* ScentdMatch */}
                        {(() => {
                          const myLikedIds = new Set(
                            journalEntries
                              .filter(e => e.rating === 'like' || e.rating === 'love')
                              .map(e => e.fragranceId)
                          );
                          const friendLoved = friendJournal.filter(e => e.rating === 'love');
                          const friendLiked = friendJournal.filter(e => e.rating === 'like');
                          const inCommon = friendJournal.filter(e => myLikedIds.has(e.fragranceId));

                          return (
                            <div className="mt-4 bg-white border border-cream-dark rounded-xl overflow-hidden">
                              <div className="px-5 pt-5 pb-4">
                                <p className="text-xs tracking-widest uppercase text-stone-light mb-1">ScentdMatch</p>
                                <p className="font-serif text-lg text-charcoal mb-4">
                                  {viewingMember.shareJournal === false
                                    ? `${viewingMember.firstName} keeps their journal private`
                                    : friendJournal.length === 0
                                    ? `${viewingMember.firstName} hasn't journaled yet`
                                    : inCommon.length > 0
                                    ? `You share ${inCommon.length} fragrance${inCommon.length !== 1 ? 's' : ''}`
                                    : `No shared fragrances yet`}
                                </p>

                                {friendJournal.length > 0 && (
                                  <>
                                    {inCommon.length > 0 && (
                                      <div className="mb-4">
                                        <p className="text-xs tracking-widest uppercase text-stone-light mb-2">In common</p>
                                        <div className="flex flex-wrap gap-1.5">
                                          {inCommon.map(e => (
                                            <span key={e.fragranceId} className="text-xs bg-cream-dark text-charcoal px-3 py-1 rounded-full">
                                              {e.fragranceName}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {friendLoved.length > 0 && (
                                      <div className="mb-4">
                                        <p className="text-xs tracking-widest uppercase text-stone-light mb-2">★ {viewingMember.firstName}'s All-Time Faves</p>
                                        <div className="flex flex-wrap gap-1.5">
                                          {friendLoved.map(e => (
                                            <span key={e.fragranceId} className="text-xs bg-cream-dark text-charcoal px-3 py-1 rounded-full">
                                              {e.fragranceName}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {friendLiked.length > 0 && (
                                      <div>
                                        <p className="text-xs tracking-widest uppercase text-stone-light mb-2 flex items-center gap-1"><IconHeart size={11} strokeWidth={1.5} /> {viewingMember.firstName}'s Liked</p>
                                        <div className="flex flex-wrap gap-1.5">
                                          {friendLiked.map(e => (
                                            <span key={e.fragranceId} className="text-xs bg-cream-dark text-charcoal px-3 py-1 rounded-full">
                                              {e.fragranceName}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })()}

                        <p className="text-xs text-stone-light text-center mt-8 tracking-widest">SCENTD</p>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })()}

        {/* Tray journal lightbox */}
        {trayJournalEntry && (() => {
          const fragrance = catalog.find(f => f.fragranceId === trayJournalEntry.fragranceId);
          if (!fragrance) { setTrayJournalEntry(null); return null; }
          return (
            <JournalEntryPanel
              fragrance={fragrance}
              userId={userId}
              authToken={authToken}
              existingEntry={trayJournalEntry}
              editLog={null}
              onClose={() => setTrayJournalEntry(null)}
              onSaved={() => { setTrayJournalEntry(null); onCatalogUpdated(); }}
            />
          );
        })()}

        {activeSection === 'wishlist' && activeMainTab === 'myScent' && (
          <Wishlist
            userId={userId}
            authToken={authToken}
            wishlistIds={wishlistIds}
            journalEntries={journalEntries}
            catalog={catalog}
            ingredients={ingredients}
            recommendedIds={recommendedIds}
            challengeId={challengeId}
            onWishlistToggle={onWishlistToggle}
            onAddToJournal={onAddToJournal}
          />
        )}

        {activeSection === 'tray' && activeMainTab === 'myScent' && (() => {
          const ownedEntries = journalEntries.filter(entry => {
            const logs: any[] = entry.wearLogs || [];
            const hasOwnership = logs.some((log: any) =>
              (log.ownership || []).some((o: string) => o === 'bottle' || o === 'sample')
            );
            const legacy = (entry as any).ownershipStatus === 'full_bottle' || (entry as any).ownershipStatus === 'sample';
            return hasOwnership || legacy;
          });

          const getOwnershipBadge = (entry: JournalEntry): 'bottle' | 'sample' => {
            const logs: any[] = entry.wearLogs || [];
            const last = logs[logs.length - 1];
            if (last?.ownership?.includes('bottle')) return 'bottle';
            if ((entry as any).ownershipStatus === 'full_bottle') return 'bottle';
            return 'sample';
          };

          const guessNoteCategory = (note: string): string | null => {
            const n = note.toLowerCase();
            if (['rose','jasmine','lily','violet','iris','tuberose','peony','gardenia','magnolia','neroli','ylang','orchid','freesia','carnation','geranium','narcissus','mimosa','osmanthus','honeysuckle','heliotrope'].some(w => n.includes(w))) return 'Florals';
            if (['cedar','sandalwood','vetiver','oud','patchouli','oak','birch','pine','wood','guaiac','agarwood','hinoki','spruce','fir','cypriol','rosewood','bamboo','cypress','driftwood','papyrus'].some(w => n.includes(w))) return 'Woods';
            if (['bergamot','lemon','orange','grapefruit','lime','mandarin','citrus','yuzu','clementine','petitgrain','lemon verbena'].some(w => n.includes(w))) return 'Citrus';
            if (['pepper','cardamom','cinnamon','clove','nutmeg','ginger','saffron','cumin','coriander','turmeric','anise','davana'].some(w => n.includes(w))) return 'Spices';
            if (['amber','benzoin','frankincense','myrrh','resin','labdanum','incense','opoponax','cistus','balsam','ambrox','ambroxan','styrax','smoke','aldehyde'].some(w => n.includes(w))) return 'Resins & Orientals';
            if (['musk','ambrette','ambergris','civet','castoreum','cashmeran','leather','suede'].some(w => n.includes(w))) return 'Musks';
            if (['grass','moss','fern','aquatic','marine','sea','green','basil','mint','sage','lavender','rosemary','thyme','galbanum','hay','oakmoss','angelica','juniper','eucalyptus','rain','ozone','earth'].some(w => n.includes(w))) return 'Greens & Aquatics';
            if (['apple','peach','pear','cherry','plum','berry','fig','mango','coconut','lychee','melon','cassis','blackcurrant','raspberry','strawberry','apricot','rhubarb','pineapple','pomegranate'].some(w => n.includes(w))) return 'Fruits';
            if (['tobacco','vanilla','honey','coffee','almond','praline','tonka','rum','cocoa','sugar','caramel','chocolate','beeswax','marshmallow'].some(w => n.includes(w))) return 'Tobacco & Gourmands';
            return null;
          };

          const getCategories = (entry: JournalEntry): string[] => {
            const frag = catalog.find(f => f.fragranceId === entry.fragranceId);
            if (!frag) return [];
            const allNotes = [...(frag.topNotes || []), ...(frag.middleNotes || []), ...(frag.baseNotes || []), ...(frag.notes || [])];
            return Array.from(new Set(allNotes.map((n: string) => {
              // First try the ingredients table for an exact category
              const ing = ingredients.find(i =>
                i.ingredientId === n.toLowerCase().replace(/\s+/g, '-') || i.name.toLowerCase() === n.toLowerCase()
              );
              return ing?.category ?? guessNoteCategory(n);
            }).filter(Boolean))) as string[];
          };

          const getCatColor = (entry: JournalEntry): string => {
            const cats = getCategories(entry);
            if (cats.some(c => c.includes('Floral'))) return '#D4A0A8';
            if (cats.some(c => c.includes('Wood') || c.includes('Green'))) return '#8CA87A';
            if (cats.some(c => c.includes('Citrus') || c.includes('Fruit'))) return '#89C4D4';
            if (cats.some(c => c.includes('Resin') || c.includes('Oriental') || c.includes('Gourmand') || c.includes('Tobacco'))) return '#C4A882';
            if (cats.some(c => c.includes('Musk'))) return '#5A5460';
            return '#C4A882';
          };

          const LAYER_RULES: Array<{ a: string; b: string; reason: string }> = [
            { a: 'Citrus', b: 'Woods', reason: 'Fresh citrus lifts the warmth of woods into something alive and modern.' },
            { a: 'Citrus', b: 'Musks', reason: 'Citrus over soft musk creates a clean, skin-close radiance.' },
            { a: 'Florals', b: 'Woods', reason: 'Florals ground into woods — romantic and long-lasting.' },
            { a: 'Florals', b: 'Musks', reason: 'A classic pairing: petals on skin, intimate and soft.' },
            { a: 'Spices', b: 'Woods', reason: 'Spice and wood together build a rich, confident depth.' },
            { a: 'Resins', b: 'Citrus', reason: 'Resinous warmth cut with citrus — mysterious but approachable.' },
            { a: 'Tobacco', b: 'Florals', reason: 'Sweetness and florals blur into something deeply intimate.' },
            { a: 'Greens', b: 'Musks', reason: 'Fresh green over musk reads like clean skin after rain.' },
          ];

          const CATEGORY_WEIGHT: Record<string, number> = {
            'Resins & Orientals': 6, 'Tobacco & Gourmands': 5,
            'Woods': 4, 'Musks': 3, 'Spices': 3,
            'Florals': 2, 'Fruits': 1, 'Citrus': 1, 'Greens & Aquatics': 0,
          };
          const fragWeight = (cats: string[]) =>
            cats.reduce((max, c) => Math.max(max, CATEGORY_WEIGHT[c] ?? 1), 0);

          const sprayLocation = (cats: string[]): string => {
            const w = fragWeight(cats);
            if (w >= 4) return 'Chest · inner elbows';
            if (w >= 2) return 'Neck · chest';
            return 'Neck · wrists';
          };

          const pairingTip = (catsA: string[], catsB: string[]): string => {
            const all = [...catsA, ...catsB];
            if (all.some(c => c.includes('Citrus') || c.includes('Greens')))
              return "Don\'t rub your wrists together — it crushes the top notes. Let them settle naturally.";
            if (all.some(c => c.includes('Resin') || c.includes('Tobacco')))
              return "Less is more with rich bases — one spray of each is enough. This combination deepens over time.";
            if (all.some(c => c.includes('Wood')) && all.some(c => c.includes('Floral')))
              return "Let each fragrance fully dry before layering the next — about 30 seconds between each.";
            if (all.some(c => c.includes('Musk')))
              return "Apply to warm, moisturised skin for the best result — musks amplify on hydrated skin.";
            if (all.some(c => c.includes('Spice')))
              return "This combination deepens over the first hour — give it time before judging the balance.";
            return "Less is more when layering — one spray of each is enough to start.";
          };

          const d = new Date();
          const seed = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();

          // Seeded shuffle for daily variety — different fragrances surface each day
          const ownedWithCats = ownedEntries.map(e => ({ entry: e, cats: getCategories(e) }));
          const shuffled = [...ownedWithCats];
          for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.abs((seed * (i + 1) * 2654435761) ^ (seed >> 16)) % (i + 1);
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
          }

          // Build all complementary pairs from the shuffled order
          const allPairs: Array<{ a: (typeof ownedWithCats)[0]; b: (typeof ownedWithCats)[0]; reason: string }> = [];
          for (let i = 0; i < shuffled.length; i++) {
            for (let j = i + 1; j < shuffled.length; j++) {
              const ea = shuffled[i];
              const eb = shuffled[j];
              for (const rule of LAYER_RULES) {
                const match =
                  (ea.cats.some(c => c.includes(rule.a)) && eb.cats.some(c => c.includes(rule.b))) ||
                  (eb.cats.some(c => c.includes(rule.a)) && ea.cats.some(c => c.includes(rule.b)));
                if (match) { allPairs.push({ a: ea, b: eb, reason: rule.reason }); break; }
              }
            }
          }

          const todayPair = allPairs.length > 0 ? allPairs[0]
            : (shuffled.length >= 2 ? { a: shuffled[0], b: shuffled[1], reason: 'A combination worth exploring.' } : null);

          const selectedEntry = traySelected ? ownedEntries.find(e => e.fragranceId === traySelected) : null;
          const selectedPairs = selectedEntry
            ? allPairs.filter(p =>
                p.a.entry.fragranceId === selectedEntry.fragranceId ||
                p.b.entry.fragranceId === selectedEntry.fragranceId
              ).slice(0, 4)
            : [];

          // Render the expanded how-to-wear instructions (shown inside collapsible)
          const renderHowToWear = (eA: (typeof ownedWithCats)[0], eB: (typeof ownedWithCats)[0]) => {
            const wA = fragWeight(eA.cats);
            const wB = fragWeight(eB.cats);
            const [first, firstCats, second, secondCats] = wA >= wB
              ? [eA.entry, eA.cats, eB.entry, eB.cats]
              : [eB.entry, eB.cats, eA.entry, eA.cats];
            const tip = pairingTip(firstCats, secondCats);
            return (
              <div className="mt-3 pt-3 border-t border-cream-dark space-y-3">
                <div className="flex gap-3 items-start">
                  <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#C4845A', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, fontFamily: 'system-ui', flexShrink: 0, marginTop: 1 }}>1</div>
                  <div>
                    <p className="text-xs font-medium text-charcoal">{first.fragranceName} — {sprayLocation(firstCats)}</p>
                    <p className="text-xs text-stone-light mt-0.5 leading-relaxed">
                      {firstCats.some(c => ['Resins & Orientals','Woods','Tobacco & Gourmands'].includes(c))
                        ? 'Apply first. Heavier base notes anchor the skin — let dry 30s before layering.'
                        : 'Apply to pulse points and let it open on your skin.'}
                    </p>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#7A9E8E', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, fontFamily: 'system-ui', flexShrink: 0, marginTop: 1 }}>2</div>
                  <div>
                    <p className="text-xs font-medium text-charcoal">{second.fragranceName} — {sprayLocation(secondCats)}</p>
                    <p className="text-xs text-stone-light mt-0.5 leading-relaxed">
                      {secondCats.some(c => ['Citrus','Greens & Aquatics','Florals'].includes(c))
                        ? 'Layer on top. Lighter notes project best from pulse points.'
                        : 'Layer over the base to build depth and extend the wear.'}
                    </p>
                  </div>
                </div>
                <p className="text-xs leading-relaxed rounded-lg px-3 py-2" style={{ background: '#F5F0EA', color: '#7A6E65' }}>✦ {tip}</p>
              </div>
            );
          };

          return (
            <div>
              {ownedEntries.length === 0 ? (
                <div className="text-center py-16">
                  <p className="text-stone mb-2">Your Scent Tray is empty.</p>
                  <p className="text-stone-light text-sm">Log a fragrance and mark it as a bottle or sample to add it here.</p>
                </div>
              ) : (
                <>
                  {/* 1. Today's Pairing */}
                  {todayPair && (
                    <div className="bg-white border border-cream-dark rounded-xl overflow-hidden mb-4"
                      style={{ borderColor: openLayerPair === -1 ? '#C4A882' : undefined }}>
                      <div
                        className="px-5 pt-4 pb-3 cursor-pointer"
                        onClick={() => setOpenLayerPair(openLayerPair === -1 ? null : -1)}
                      >
                        <div className="flex justify-between items-center mb-2">
                          <p className="text-xs tracking-widest uppercase text-stone-light">Today's Pairing</p>
                          <div className="flex items-center gap-2">
                            <p className="text-xs text-stone-light">{d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
                            <span className="text-stone-light text-xs" style={{ display: 'inline-block', transition: 'transform 0.2s', transform: openLayerPair === -1 ? 'rotate(180deg)' : 'none' }}>▾</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex-1 rounded-lg px-3 py-2 text-center" style={{ background: `${getCatColor(todayPair.a.entry)}18` }}>
                            <p className="text-xs font-medium text-charcoal leading-snug">{todayPair.a.entry.fragranceName}</p>
                            <p className="text-[9px] text-stone-light mt-0.5">{todayPair.a.entry.fragranceHouse}</p>
                          </div>
                          <span className="text-stone-light flex-shrink-0">+</span>
                          <div className="flex-1 rounded-lg px-3 py-2 text-center" style={{ background: `${getCatColor(todayPair.b.entry)}18` }}>
                            <p className="text-xs font-medium text-charcoal leading-snug">{todayPair.b.entry.fragranceName}</p>
                            <p className="text-[9px] text-stone-light mt-0.5">{todayPair.b.entry.fragranceHouse}</p>
                          </div>
                        </div>
                        <p className="text-xs text-stone-light mt-2 leading-relaxed">{todayPair.reason}</p>
                      </div>
                      {openLayerPair === -1 && (
                        <div className="px-5 pb-4" onClick={e => e.stopPropagation()}>
                          {renderHowToWear(todayPair.a, todayPair.b)}
                        </div>
                      )}
                      <p className="text-[9px] tracking-widest uppercase text-stone-light px-5 pb-3">New pairing tomorrow</p>
                    </div>
                  )}

                  {/* 2. Scent cabinet — category legend + tappable bottles */}
                  <div className="bg-white border border-cream-dark rounded-xl p-4 mb-4">
                    <div className="flex items-baseline justify-between mb-3">
                      <p className="text-xs tracking-widest uppercase text-stone-light">Your collection — {ownedEntries.length}</p>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 justify-end">
                        {([['#89C4D4','Air'],['#D4A0A8','Bloom'],['#C4A882','Velvet'],['#8CA87A','Terrain'],['#5A5460','Noir']] as [string,string][]).map(([color, label]) => (
                          <span key={label} className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }}/>
                            <span className="text-[9px] text-stone-light tracking-wide">{label}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {ownedEntries.map(entry => {
                        const color = getCatColor(entry);
                        const badge = getOwnershipBadge(entry);
                        const isSelected = traySelected === entry.fragranceId;
                        return (
                          <div key={entry.fragranceId} className="relative">
                            <button
                              onClick={() => { setTraySelected(isSelected ? null : entry.fragranceId); setTrayPartnerSelected(null); }}
                              className="w-full bg-cream rounded-xl p-2 flex flex-col items-center gap-1 transition-all"
                              style={{ border: `1.5px solid ${isSelected ? '#2A2520' : 'transparent'}` }}
                            >
                              <div className="w-12 h-14 rounded-lg flex items-center justify-center" style={{ background: `${color}22` }}>
                                <svg width="22" height="34" viewBox="0 0 22 32" fill="none">
                                  <rect x="7" y="0" width="8" height="5" rx="2" fill={color}/>
                                  <path d="M4 8 Q4 6 7 6 L15 6 Q18 6 18 8 L20 28 Q20 31 17 31 L5 31 Q2 31 2 28 Z" fill={color} opacity="0.3"/>
                                  <path d="M4 8 Q4 6 7 6 L15 6 Q18 6 18 8 L20 28 Q20 31 17 31 L5 31 Q2 31 2 28 Z" fill="none" stroke={color} strokeWidth="1.3"/>
                                </svg>
                              </div>
                              <p className="text-[8px] text-center text-charcoal leading-tight font-medium line-clamp-2">{entry.fragranceName}</p>
                              <span className="text-[7px] tracking-widest uppercase rounded-full px-1.5 py-0.5"
                                style={badge === 'bottle' ? { background: '#E8F0E8', color: '#3A6A3A' } : { background: '#F0E4CC', color: '#7A6030' }}>
                                {badge === 'bottle' ? 'Bottle' : 'Sample'}
                              </span>
                            </button>
                            {/* Journal shortcut */}
                            <button
                              onClick={e => { e.stopPropagation(); setTrayJournalEntry(entry); }}
                              className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-white border border-cream-dark flex items-center justify-center hover:border-stone transition-colors"
                              title="Log a wear session"
                            >
                              <IconHeart size={9} strokeWidth={1.5} className="text-stone-light" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    {traySelected && <p className="text-[9px] text-stone-light text-center mt-2 tracking-wide">Tap again to deselect · scroll down to see pairings</p>}
                  </div>

                  {/* 3. Explore a pairing — hub and spoke */}
                  <div className="bg-white border border-cream-dark rounded-xl p-4">
                    {!selectedEntry ? (
                      <p className="text-xs text-stone-light text-center py-2">Tap any bottle above to explore pairings</p>
                    ) : (
                      <>
                        {/* Hub — selected fragrance */}
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${getCatColor(selectedEntry)}22` }}>
                            <svg width="18" height="28" viewBox="0 0 22 32" fill="none">
                              <rect x="7" y="0" width="8" height="5" rx="2" fill={getCatColor(selectedEntry)}/>
                              <path d="M4 8 Q4 6 7 6 L15 6 Q18 6 18 8 L20 28 Q20 31 17 31 L5 31 Q2 31 2 28 Z" fill={getCatColor(selectedEntry)} opacity="0.3"/>
                              <path d="M4 8 Q4 6 7 6 L15 6 Q18 6 18 8 L20 28 Q20 31 17 31 L5 31 Q2 31 2 28 Z" fill="none" stroke={getCatColor(selectedEntry)} strokeWidth="1.3"/>
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-charcoal">{selectedEntry.fragranceName}</p>
                            <p className="text-xs text-stone-light">{selectedEntry.fragranceHouse}</p>
                          </div>
                        </div>

                        {selectedPairs.length > 0 ? (
                          <>
                            {/* Spoke chips */}
                            <p className="text-[9px] tracking-widest uppercase text-stone-light mb-2">Pairs well with</p>
                            <div className="flex flex-wrap gap-2 mb-4">
                              {selectedPairs.map((pair, i) => {
                                const partner = pair.a.entry.fragranceId === selectedEntry.fragranceId ? pair.b : pair.a;
                                const isActive = trayPartnerSelected === partner.entry.fragranceId;
                                return (
                                  <button
                                    key={i}
                                    onClick={() => setTrayPartnerSelected(isActive ? null : partner.entry.fragranceId)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-all"
                                    style={{
                                      border: `1.5px solid ${isActive ? '#2A2520' : '#EDE8DF'}`,
                                      background: isActive ? '#2A2520' : '#fff',
                                      color: isActive ? '#FAF7F2' : '#6B6460',
                                    }}
                                  >
                                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: isActive ? '#FAF7F2' : getCatColor(partner.entry), display: 'inline-block', flexShrink: 0 }}/>
                                    {partner.entry.fragranceName}
                                  </button>
                                );
                              })}
                            </div>

                            {/* Instructions for active partner */}
                            {(() => {
                              const activePair = selectedPairs.find(p =>
                                p.a.entry.fragranceId === trayPartnerSelected ||
                                p.b.entry.fragranceId === trayPartnerSelected
                              );
                              if (!activePair) return (
                                <p className="text-xs text-stone-light text-center py-2">Tap a fragrance above to see how to layer it</p>
                              );
                              const partner = activePair.a.entry.fragranceId === selectedEntry.fragranceId ? activePair.b : activePair.a;
                              const selOwned = ownedWithCats.find(o => o.entry.fragranceId === selectedEntry.fragranceId)!;
                              return (
                                <div className="bg-cream rounded-xl p-4">
                                  <p className="text-xs text-stone-light leading-relaxed mb-3">{activePair.reason}</p>
                                  {renderHowToWear(selOwned, partner)}
                                </div>
                              );
                            })()}
                          </>
                        ) : (
                          <p className="text-xs text-stone-light text-center py-2">No complementary pairings found with your current collection.</p>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {activeSection === 'scent' && activeMainTab === 'myScent' && (
          <>
            {/* Sticky internal scroll nav — only when profile exists */}
            {profile && (
              <div ref={scrollNavRef} className="sticky top-[61px] z-20 bg-cream border-b border-cream-dark mb-6 -mx-6 px-6" style={{ scrollbarWidth: 'none' }}>
                <div className="flex gap-6 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                  {scrollSections.map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => scrollToSection(key)}
                      className={`text-xs tracking-widest uppercase py-3 flex-shrink-0 border-b-2 transition-colors ${
                        activeScrollSection === key
                          ? 'text-charcoal border-charcoal'
                          : 'text-stone-light border-transparent hover:text-stone'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!profile ? (
              <div className="text-center py-16">
                <p className="text-stone mb-4">Log some fragrances, and your Scentd Concierge will prepare your Scent Identity.</p>
                <p className="text-stone-light text-sm mb-2">The more you log, the more precise your reading becomes.</p>
                <p className="text-stone-light text-sm mb-8">
                  You have <span className="text-charcoal">{journalEntries.length}</span> {journalEntries.length === 1 ? 'fragrance' : 'fragrances'} in your journal.
                </p>
                <button
                  onClick={generateProfile}
                  disabled={generating || journalEntries.length === 0}
                  className="bg-charcoal text-cream px-8 py-4 text-sm tracking-widest uppercase rounded-xl hover:bg-charcoal-light transition-colors disabled:opacity-50"
                >
                  {generating ? 'Your Concierge is reading your journal...' : 'Meet your Concierge'}
                </button>
                {generating && (
                  <p className="text-stone-light text-xs mt-3 animate-pulse">Your Concierge is preparing your Scent Identity — this takes about 15 seconds.</p>
                )}
                {journalEntries.length === 0 && (
                  <p className="text-stone-light text-xs mt-3">Add at least one fragrance to get started</p>
                )}
                {error && <p className="text-red-400 text-sm mt-4">{error}</p>}
              </div>
            ) : (
              <div>
                {/* Scent identity */}
                <div ref={el => { scrollSectionRefs.current.identity = el; }} className="bg-white border border-cream-dark rounded-xl p-8 mb-6">
                  <p className="text-xs tracking-widest uppercase text-stone-light mb-1">Your Scent Identity</p>
                  <p className="text-xs text-stone-light mb-4">
                    Prepared by your Scentd Concierge
                    {profile.generatedAt && <span className="ml-2">· Updated {timeAgo(profile.generatedAt)}</span>}
                  </p>
                  <p className="text-charcoal leading-relaxed font-serif text-lg">{profile.profile}</p>
                </div>

                {/* Note to Self */}
                {profile.noteToSelf && profile.noteToSelf.length > 0 && (
                  <div className="bg-white border-l-4 border-cream-dark rounded-xl p-5 mb-6" style={{ borderLeftColor: '#C9A96E' }}>
                    <p className="text-xs tracking-widest uppercase mb-3" style={{ color: '#C9A96E' }}>Note to Self</p>
                    <div className="space-y-3">
                      {profile.noteToSelf.map((note, i) => (
                        <div key={i} className="flex gap-3 items-start">
                          <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5" style={{ background: '#C9A96E' }} />
                          <p className="text-sm text-stone leading-relaxed">{note}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-stone-light mt-3">Refreshes with your Scent Identity</p>
                  </div>
                )}

                {/* Scent family breakdown */}
                {scentFamilyData.length > 0 && (
                  <div ref={el => { scrollSectionRefs.current.families = el; }} className="bg-white border border-cream-dark rounded-xl p-6 mb-6">
                    <p className="text-xs tracking-widest uppercase text-stone-light mb-4">Your scent families</p>
                    <p className="text-stone-light text-xs mb-4">Based on ingredients in your liked fragrances</p>
                    <div className="space-y-3">
                      {scentFamilyData.map(({ category, pct }) => (
                        <div key={category}>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-xs tracking-widest uppercase text-stone">{category}</span>
                            <span className="text-xs text-stone-light">{pct}%</span>
                          </div>
                          <div className="h-1.5 bg-cream-dark rounded-full">
                            <div
                              className="h-full bg-charcoal rounded-full transition-all duration-500"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Scent Identity Map ── */}
                {scentMapData && (
                  <div ref={el => { scrollSectionRefs.current.map = el; }} className="mb-6">
                    <div className="bg-white border border-cream-dark rounded-xl rounded-b-none p-8 mb-0">
                      <p className="text-xs tracking-widest uppercase text-stone-light mb-3">Your scent archetype</p>
                      <p className="font-serif text-2xl text-charcoal mb-3">{scentMapData.archetype.name}</p>
                      <p className="text-stone text-sm leading-relaxed mb-2">{scentMapData.archetype.tagline}</p>
                      <p className="text-stone-light text-sm leading-relaxed">{scentMapData.archetype.description}</p>
                    </div>
                    <div className="bg-white border border-cream-dark border-t-0 rounded-xl rounded-t-none px-6 pb-6">
                      <div className="flex justify-center">
                        <div className="relative w-full" style={{ maxWidth: SVG_SIZE, aspectRatio: '1' }}>
                          <svg width="100%" height="100%" viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`} preserveAspectRatio="xMidYMid meet" style={{ display: 'block' }}>
                            <defs>
                              <radialGradient id="centerGlow2" cx="50%" cy="50%" r="50%">
                                <stop offset="0%" stopColor={scentMapData.archetype.color} stopOpacity="0.35" />
                                <stop offset="100%" stopColor={scentMapData.archetype.color} stopOpacity="0" />
                              </radialGradient>
                              <radialGradient id="dotGlow2" cx="50%" cy="50%" r="50%">
                                <stop offset="0%" stopColor="#2C2C2C" stopOpacity="0.15" />
                                <stop offset="100%" stopColor="#2C2C2C" stopOpacity="0" />
                              </radialGradient>
                            </defs>
                            <rect x="1" y="1" width={SVG_SIZE - 2} height={SVG_SIZE - 2} fill="none" stroke="#E8E0D5" strokeWidth="1" />
                            <line x1={CENTER} y1="20" x2={CENTER} y2={SVG_SIZE - 20} stroke="#E8E0D5" strokeWidth="1" />
                            <line x1="20" y1={CENTER} x2={SVG_SIZE - 20} y2={CENTER} stroke="#E8E0D5" strokeWidth="1" />
                            <text x={CENTER} y="15" textAnchor="middle" fill="#7A7060" fontSize="9" fontFamily="Inter, sans-serif" letterSpacing="2.5">ETHEREAL</text>
                            <text x={CENTER} y={SVG_SIZE - 5} textAnchor="middle" fill="#7A7060" fontSize="9" fontFamily="Inter, sans-serif" letterSpacing="2.5">GROUNDED</text>
                            <text x="9" y={CENTER + 2} textAnchor="middle" fill="#7A7060" fontSize="9" fontFamily="Inter, sans-serif" letterSpacing="2.5" transform={`rotate(-90, 9, ${CENTER})`}>INTIMATE</text>
                            <text x={SVG_SIZE - 9} y={CENTER + 2} textAnchor="middle" fill="#7A7060" fontSize="9" fontFamily="Inter, sans-serif" letterSpacing="2.5" transform={`rotate(90, ${SVG_SIZE - 9}, ${CENTER})`}>PROJECTIVE</text>
                            <circle cx={toSvg(scentMapData.cx, 'x')} cy={toSvg(scentMapData.cy, 'y')} r="28" fill="url(#centerGlow2)" />
                            {scentMapData.dots.map(dot => {
                              const sx = toSvg(dot.x, 'x');
                              const sy = toSvg(dot.y, 'y');
                              const isLove = dot.rating === 'love';
                              const isLiked = dot.rating === 'like';
                              const isHovered = hoveredDot === dot.id;
                              return (
                                <g key={dot.id} style={{ cursor: 'pointer' }}
                                  onMouseEnter={() => setHoveredDot(dot.id)}
                                  onMouseLeave={() => setHoveredDot(null)}
                                >
                                  {isHovered && <circle cx={sx} cy={sy} r="10" fill="url(#dotGlow2)" />}
                                  {isLove ? (
                                    // Star shape for all-time faves
                                    <text x={sx} y={sy + 4} textAnchor="middle" fontSize="10" fill={scentMapData.archetype.color} opacity="0.95">★</text>
                                  ) : (
                                    <circle
                                      cx={sx} cy={sy}
                                      r={isLiked ? 3.5 : 2}
                                      fill={isLiked ? '#2C2C2C' : '#B0A090'}
                                      opacity={isLiked ? 0.85 : 0.6}
                                    />
                                  )}
                                </g>
                              );
                            })}
                            <circle cx={toSvg(scentMapData.cx, 'x')} cy={toSvg(scentMapData.cy, 'y')} r="5" fill="none" stroke={scentMapData.archetype.color} strokeWidth="1.5" opacity="0.9" />
                            <circle cx={toSvg(scentMapData.cx, 'x')} cy={toSvg(scentMapData.cy, 'y')} r="2" fill={scentMapData.archetype.color} opacity="0.9" />
                          </svg>
                          {hoveredDot && (() => {
                            const dot = scentMapData.dots.find(d => d.id === hoveredDot);
                            if (!dot) return null;
                            return (
                              <div className="absolute bottom-2 left-0 right-0 text-center pointer-events-none">
                                <span className="text-xs text-stone font-serif">{dot.name}</span>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="flex justify-center gap-6 mt-2">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-full bg-charcoal" style={{ opacity: 0.85 }} />
                          <span className="text-xs text-stone">Liked</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#B0A090' }} />
                          <span className="text-xs text-stone">Neutral</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span style={{ color: scentMapData.archetype.color, fontSize: 11, lineHeight: 1 }}>★</span>
                          <span className="text-xs text-stone">All-time fave</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-2.5 h-2.5 rounded-full border" style={{ borderColor: scentMapData.archetype.color }} />
                          <span className="text-xs text-stone">Your center</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Archetype Rainbow Bar ── */}
                {scentMapData && (() => {
                  const userIdx = ARCHETYPES.indexOf(scentMapData.archetype);
                  const activeIdx = selectedArchetypeIdx !== null ? selectedArchetypeIdx : userIdx;
                  const active = ARCHETYPES[activeIdx];
                  const visualPcts = [18, 14, 12, 11, 10, 9, 9, 8, 5, 4];
                  const totalGap = (ARCHETYPES.length - 1) * 2;
                  const totalUsers = Object.values(localArchetypeCounts).reduce((s, n) => s + n, 0);
                  const realPct = (name: string) => totalUsers > 0 ? Math.round((localArchetypeCounts[name] || 0) / totalUsers * 100) : null;
                  const activePct = realPct(active.name);
                  return (
                    <div className="bg-white border border-cream-dark rounded-xl p-5 mb-6">
                      <div className="flex justify-between items-baseline mb-1">
                        <p className="text-xs tracking-widest uppercase text-stone-light">Scent Archetypes</p>
                        <p className="text-xs text-stone-light">Tap any block</p>
                      </div>
                      {(() => {
                        const total = Object.values(localArchetypeCounts).reduce((s, n) => s + n, 0);
                        const mine = localArchetypeCounts[scentMapData.archetype.name] || 0;
                        const pct = total > 0 ? Math.round((mine / total) * 100) : null;
                        return pct !== null ? (
                          <p className="text-stone-light text-xs mb-3">You are like {pct}% of our users.</p>
                        ) : <div className="mb-3" />;
                      })()}
                      {/* Percentage labels above bars */}
                      <div className="flex items-end mb-1" style={{ gap: 2 }}>
                        {ARCHETYPES.map((a, i) => {
                          const pct = realPct(a.name);
                          return (
                            <div
                              key={a.name}
                              style={{ width: `calc(${visualPcts[i]}% - ${(totalGap * visualPcts[i] / 100).toFixed(2)}px)`, flexShrink: 0 }}
                              className="flex justify-center"
                            >
                              {pct !== null && pct > 0 && (
                                <span style={{ fontSize: 9, color: a.color, fontWeight: 500, lineHeight: 1 }}>{pct}%</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {/* Rainbow bar */}
                      <div className="flex items-center" style={{ height: 38, gap: 2 }}>
                        {ARCHETYPES.map((a, i) => {
                          const isYours = i === userIdx;
                          const isActive = i === activeIdx;
                          return (
                            <div
                              key={a.name}
                              onClick={() => setSelectedArchetypeIdx(i)}
                              style={{
                                width: `calc(${visualPcts[i]}% - ${(totalGap * visualPcts[i] / 100).toFixed(2)}px)`,
                                flexShrink: 0,
                                background: a.color,
                                height: isYours ? 38 : 26,
                                borderRadius: i === 0 ? '8px 0 0 8px' : i === ARCHETYPES.length - 1 ? '0 8px 8px 0' : 3,
                                opacity: isActive ? 1 : 0.45,
                                boxShadow: isYours ? `0 3px 10px ${a.color}66` : 'none',
                                cursor: 'pointer',
                                transition: 'opacity 0.15s',
                              }}
                            />
                          );
                        })}
                      </div>
                      {/* Detail card */}
                      <div className="mt-4 rounded-xl p-4" style={{ background: active.color + '18', borderLeft: `3px solid ${active.color}` }}>
                        <p className="font-serif text-base text-charcoal mb-1">
                          {active.name}
                          {activeIdx === userIdx && (
                            <span className="ml-2 text-xs tracking-widest uppercase text-white px-2 py-0.5 rounded-full align-middle" style={{ background: active.color, fontSize: 8 }}>You</span>
                          )}
                        </p>
                        <p className="text-xs mb-2" style={{ color: active.color }}>{active.tagline}</p>
                        <p className="text-xs text-stone leading-relaxed">{active.description}</p>
                        {activeIdx === userIdx && activePct !== null && (
                          <p className="text-xs text-stone-light mt-2">You're like {activePct}% of our members.</p>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Zodiac */}
                {profile.zodiac && (
                  <div className="bg-cream-dark border border-cream-dark p-6 mb-6">
                    <p className="text-xs tracking-widest uppercase text-stone-light mb-2">✨ Just for fun</p>
                    <p className="text-stone leading-relaxed text-sm">{profile.zodiac}</p>
                  </div>
                )}

                {/* Recommendations */}
                <p ref={el => { scrollSectionRefs.current.foryou = el; }} className="text-xs tracking-widest uppercase text-stone-light mb-4">You might love these</p>
                <div className="space-y-3 mb-6">
                  {(profile.recommendations || []).map((rec, i) => {
                    const fragId = resolveFragranceId(rec.name, rec.house);
                    const isWishlisted = wishlistIds.has(fragId);
                    const isResolving = resolvingJournal === `${rec.name}-${rec.house}`;
                    const fragKey = `${rec.name}|${rec.house}`;
                    const vote = recFeedback[fragKey];
                    const matchScore = recMatchScores.get(fragKey);
                    return (
                      <div key={i} className="bg-white border border-cream-dark rounded-xl p-5">
                        <div className="flex items-start gap-3 mb-2">
                          {/* Bottle + arc circle */}
                          <div className="flex flex-col items-center flex-shrink-0">
                            <div className="relative" style={{ width: 46, height: 46 }}>
                              <svg width="46" height="46" viewBox="0 0 46 46" style={{ position: 'absolute', inset: 0 }}>
                                <circle cx="23" cy="23" r="19" fill="none" stroke="#EDE8DF" strokeWidth="4" pathLength="100"/>
                                <circle cx="23" cy="23" r="19" fill="none" stroke="#C4A882" strokeWidth="4"
                                  pathLength="100"
                                  strokeDasharray={matchScore !== undefined ? `${matchScore} ${100 - matchScore}` : '0 100'}
                                  strokeDashoffset="25" strokeLinecap="round"/>
                                <circle cx="23" cy="23" r="15" fill="#C4A88218"/>
                              </svg>
                              <div className="absolute inset-0 flex items-center justify-center">
                                <svg width="14" height="22" viewBox="0 0 22 32" fill="none">
                                  <rect x="7" y="0" width="8" height="5" rx="2" fill="#C4A882"/>
                                  <path d="M4 8 Q4 6 7 6 L15 6 Q18 6 18 8 L20 28 Q20 31 17 31 L5 31 Q2 31 2 28 Z" fill="#C4A882" opacity="0.35"/>
                                  <path d="M4 8 Q4 6 7 6 L15 6 Q18 6 18 8 L20 28 Q20 31 17 31 L5 31 Q2 31 2 28 Z" fill="none" stroke="#C4A882" strokeWidth="1.2"/>
                                </svg>
                              </div>
                            </div>
                            {matchScore !== undefined && (
                              <span className="text-xs font-medium mt-1" style={{ color: '#8B7355' }}>{matchScore}%</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start">
                              <div className="flex-1 min-w-0">
                                <p className="font-serif text-lg text-charcoal leading-snug">{rec.name}</p>
                                <p className="text-stone-light text-sm mt-0.5">{rec.house}</p>
                              </div>
                              <div className="flex items-center gap-1.5 ml-3 flex-shrink-0">
                            <button onClick={() => handleRecFeedback(rec.name, rec.house, 'up')}
                              className="w-7 h-7 rounded-full border flex items-center justify-center transition-colors"
                              style={{ borderColor: vote === 'up' ? '#2A2520' : '#EDE8DF', background: vote === 'up' ? '#2A2520' : '#fff' }}
                              title="This sounds like me">
                              <IconThumbUp size={12} className={vote === 'up' ? 'text-cream' : 'text-stone-light'} />
                            </button>
                            <button onClick={() => handleRecFeedback(rec.name, rec.house, 'down')}
                              className="w-7 h-7 rounded-full border flex items-center justify-center transition-colors"
                              style={{ borderColor: vote === 'down' ? '#2A2520' : '#EDE8DF', background: vote === 'down' ? '#2A2520' : '#fff' }}
                              title="Not for me">
                              <IconThumbDown size={12} className={vote === 'down' ? 'text-cream' : 'text-stone-light'} />
                            </button>
                            <button onClick={() => handleAddRecToJournal(rec.name, rec.house)} disabled={isResolving}
                              className="w-7 h-7 rounded-full border border-cream-dark bg-white flex items-center justify-center hover:border-stone transition-colors disabled:opacity-40" title="Log in journal">
                              <IconHeart size={12} className="text-stone-light" />
                            </button>
                            <button onClick={() => handleWishlistRec(rec.name, rec.house)}
                              className="w-7 h-7 rounded-full border border-cream-dark bg-white flex items-center justify-center hover:border-stone transition-colors"
                              title={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}>
                              <IconPlus size={12} className={isWishlisted ? 'text-charcoal' : 'text-stone-light'} />
                            </button>
                          </div>
                            </div>
                          </div>
                        </div>
                        <p className="text-stone text-sm leading-relaxed mt-1">{rec.reason}</p>
                        {vote === 'down' && <p className="text-xs text-stone-light mt-2">Noted — your Concierge will factor this in next time.</p>}
                      </div>
                    );
                  })}
                </div>

                {/* Challenge */}
                {profile.challenge?.name && <p ref={el => { scrollSectionRefs.current.challenge = el; }} className="text-xs tracking-widest uppercase text-stone-light mb-4">Challenge yourself</p>}
                {profile.challenge?.name && (() => {
                  const fragId = resolveFragranceId(profile.challenge.name, profile.challenge.house);
                  const isWishlisted = wishlistIds.has(fragId);
                  const isResolving = resolvingJournal === `${profile.challenge.name}-${profile.challenge.house}`;
                  const fragKey = `${profile.challenge.name}|${profile.challenge.house}`;
                  const vote = recFeedback[fragKey];
                  const challengeMatchScore = recMatchScores.get(fragKey);
                  return (
                    <div className="bg-white border border-cream-dark rounded-xl p-5 mb-8">
                      <div className="flex items-start gap-3 mb-2">
                        {/* Bottle + arc circle */}
                        <div className="flex flex-col items-center flex-shrink-0">
                          <div className="relative" style={{ width: 46, height: 46 }}>
                            <svg width="46" height="46" viewBox="0 0 46 46" style={{ position: 'absolute', inset: 0 }}>
                              <circle cx="23" cy="23" r="19" fill="none" stroke="#EDE8DF" strokeWidth="4" pathLength="100"/>
                              <circle cx="23" cy="23" r="19" fill="none" stroke="#C4A882" strokeWidth="4"
                                pathLength="100"
                                strokeDasharray={challengeMatchScore !== undefined ? `${challengeMatchScore} ${100 - challengeMatchScore}` : '0 100'}
                                strokeDashoffset="25" strokeLinecap="round"/>
                              <circle cx="23" cy="23" r="15" fill="#C4A88218"/>
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <svg width="14" height="22" viewBox="0 0 22 32" fill="none">
                                <rect x="7" y="0" width="8" height="5" rx="2" fill="#C4A882"/>
                                <path d="M4 8 Q4 6 7 6 L15 6 Q18 6 18 8 L20 28 Q20 31 17 31 L5 31 Q2 31 2 28 Z" fill="#C4A882" opacity="0.35"/>
                                <path d="M4 8 Q4 6 7 6 L15 6 Q18 6 18 8 L20 28 Q20 31 17 31 L5 31 Q2 31 2 28 Z" fill="none" stroke="#C4A882" strokeWidth="1.2"/>
                              </svg>
                            </div>
                          </div>
                          {challengeMatchScore !== undefined && (
                            <span className="text-xs font-medium mt-1" style={{ color: '#8B7355' }}>{challengeMatchScore}%</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start">
                            <div className="flex-1 min-w-0">
                              <p className="font-serif text-lg text-charcoal leading-snug">{profile.challenge.name}</p>
                              <p className="text-stone-light text-sm mt-0.5">{profile.challenge.house}</p>
                            </div>
                            <div className="flex items-center gap-1.5 ml-3 flex-shrink-0">
                          <button onClick={() => handleRecFeedback(profile.challenge.name, profile.challenge.house, 'up')}
                            className="w-7 h-7 rounded-full border flex items-center justify-center transition-colors"
                            style={{ borderColor: vote === 'up' ? '#2A2520' : '#EDE8DF', background: vote === 'up' ? '#2A2520' : '#fff' }}
                            title="Worth trying">
                            <IconThumbUp size={12} className={vote === 'up' ? 'text-cream' : 'text-stone-light'} />
                          </button>
                          <button onClick={() => handleRecFeedback(profile.challenge.name, profile.challenge.house, 'down')}
                            className="w-7 h-7 rounded-full border flex items-center justify-center transition-colors"
                            style={{ borderColor: vote === 'down' ? '#2A2520' : '#EDE8DF', background: vote === 'down' ? '#2A2520' : '#fff' }}
                            title="Already know I don't like this">
                            <IconThumbDown size={12} className={vote === 'down' ? 'text-cream' : 'text-stone-light'} />
                          </button>
                          <button onClick={() => handleAddRecToJournal(profile.challenge.name, profile.challenge.house)} disabled={isResolving}
                            className="w-7 h-7 rounded-full border border-cream-dark bg-white flex items-center justify-center hover:border-stone transition-colors disabled:opacity-40" title="Log in journal">
                            <IconHeart size={12} className="text-stone-light" />
                          </button>
                          <button onClick={() => handleWishlistRec(profile.challenge.name, profile.challenge.house)}
                            className="w-7 h-7 rounded-full border border-cream-dark bg-white flex items-center justify-center hover:border-stone transition-colors"
                            title={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}>
                            <IconPlus size={12} className={isWishlisted ? 'text-charcoal' : 'text-stone-light'} />
                          </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <p className="text-stone text-sm leading-relaxed mt-1">{profile.challenge.reason}</p>
                      {vote === 'down' && <p className="text-xs text-stone-light mt-2">Noted — your Concierge will skip this territory next time.</p>}
                    </div>
                  );
                })()}

                {/* Refresh */}
                {(() => {
                  const totalSessions = journalEntries.reduce((sum, e) => sum + ((e.wearLogs?.length || 0) || 1), 0);
                  const entriesSince = totalSessions - wearLogsAtLastRefresh;
                  const isAutoReady = entriesSince >= 10;
                  const canRefresh = isAutoReady || refreshesRemaining > 0;
                  const entriesUntilAuto = Math.max(0, 10 - entriesSince);
                  return (
                    <>
                      <button
                        onClick={generateProfile}
                        disabled={generating || !canRefresh}
                        className="w-full border border-charcoal text-charcoal py-4 text-sm tracking-widest uppercase rounded-xl hover:bg-charcoal hover:text-cream transition-colors disabled:opacity-50"
                      >
                        {generating ? 'Reading your journal...' : 'Refresh your profile'}
                      </button>
                      {generating && (
                        <p className="text-stone-light text-xs mt-2 animate-pulse text-center">This takes 10–20 seconds — building your profile now.</p>
                      )}
                      <div className="mt-3 space-y-1">
                        <p className="text-stone-light text-xs">
                          {refreshesRemaining > 0
                            ? `You have ${refreshesRemaining} manual refresh${refreshesRemaining === 1 ? '' : 'es'} · Earn more with every 10 catalog additions`
                            : `No manual refreshes · Earn one with every 10 catalog additions`}
                        </p>
                        <p className="text-stone-light text-xs">
                          {isAutoReady
                            ? `✦ Your profile is refreshing automatically`
                            : `${entriesUntilAuto} journal ${entriesUntilAuto === 1 ? 'entry' : 'entries'} until your profile auto-refreshes`}
                        </p>
                      </div>
                    </>
                  );
                })()}
                {error && <p className="text-red-400 text-sm mt-3 text-center">{error}</p>}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ScentProfile;
